/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useEffect, useState } from 'react';
import {
  SortingState,
  IntegratedSorting,
  DataTypeProvider,
  SearchState,
  IntegratedFiltering,
  PagingState,
  IntegratedPaging,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  TableHeaderRow,
  Toolbar,
  SearchPanel,
  Table,
  TableColumnReordering,
  TableColumnResizing,
  PagingPanel,
} from '@devexpress/dx-react-grid-material-ui';
import { getRowId } from '../../common';
import { roles } from '../../common/check';
import {
  getPendingApprovals,
  approveDocument,
  rejectDocument,
} from '../../graphql';
import { useLazyQuery, useMutation } from '@apollo/client';
import { currencyFormatter, timeFormatter } from '../../Shared/colorFormat';
import { SearchTable, AlertLocal } from '../../components';
import DateNavigatorReports from '../../components/filters/DateNavigatorReports';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { successAlert, errorAlert } from '../../Shared/helpers';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

// Approval Status Constants
const APPROVAL_STATUS = {
  PENDING: 0,
  MANAGER_APPROVED: 1,
  ACCOUNTANT_APPROVED: 2,
  FULLY_APPROVED: 3,
  REJECTED: -1,
};

const getStatusLabel = (status: number, isRTL: boolean) => {
  switch (status) {
    case APPROVAL_STATUS.PENDING:
      return isRTL ? 'بانتظار الاعتماد' : 'Pending';
    case APPROVAL_STATUS.MANAGER_APPROVED:
      return isRTL ? 'معتمد من الإدارة' : 'Manager Approved';
    case APPROVAL_STATUS.ACCOUNTANT_APPROVED:
      return isRTL ? 'معتمد من المحاسب' : 'Accountant Approved';
    case APPROVAL_STATUS.FULLY_APPROVED:
      return isRTL ? 'معتمد بالكامل' : 'Fully Approved';
    case APPROVAL_STATUS.REJECTED:
      return isRTL ? 'مرفوض' : 'Rejected';
    default:
      return isRTL ? 'بانتظار الاعتماد' : 'Pending';
  }
};

const getStatusColor = (status: number) => {
  switch (status) {
    case APPROVAL_STATUS.PENDING:
      return '#ff9800';
    case APPROVAL_STATUS.MANAGER_APPROVED:
      return '#2196f3';
    case APPROVAL_STATUS.ACCOUNTANT_APPROVED:
      return '#4caf50';
    case APPROVAL_STATUS.FULLY_APPROVED:
      return '#4caf50';
    case APPROVAL_STATUS.REJECTED:
      return '#f44336';
    default:
      return '#ff9800';
  }
};

const ApprovalManagement = ({ isRTL, words, theme }: any) => {
  const isManager = roles.isSuperAdmin() || roles.isBranchAdmin();

  const [columns] = useState([
    { name: 'docNo', title: isRTL ? 'رقم المستند' : 'Doc No' },
    { name: 'title', title: isRTL ? 'العنوان' : 'Title' },
    { name: '_displayType', title: isRTL ? 'النوع' : 'Type' },
    { name: 'customerName', title: isRTL ? 'العميل' : 'Customer' },
    { name: 'supplierName', title: isRTL ? 'المورد' : 'Supplier' },
    { name: 'contractName', title: isRTL ? 'العقد' : 'Task/Contract' },
    { name: 'employeeName', title: isRTL ? 'المندوب' : 'Employee' },
    { name: 'amount', title: isRTL ? 'المبلغ' : 'Amount' },
    { name: 'createdAt', title: isRTL ? 'التاريخ' : 'Date' },
    { name: 'approvalStatus', title: isRTL ? 'الحالة' : 'Status' },
    { name: 'actions', title: ' ' },
  ]);

  const [tableColumnExtensions]: any = useState([
    { columnName: 'docNo', width: 150 },
    { columnName: 'title', width: 200 },
    { columnName: '_displayType', width: 120 },
    { columnName: 'customerName', width: 180 },
    { columnName: 'supplierName', width: 180 },
    { columnName: 'contractName', width: 180 },
    { columnName: 'employeeName', width: 150 },
    { columnName: 'amount', width: 130 },
    { columnName: 'createdAt', width: 150 },
    { columnName: 'approvalStatus', width: 150 },
    { columnName: 'actions', width: 200 },
  ]);

  const [rows, setRows] = useState([]);
  const [alrt, setAlrt] = useState({ show: false, msg: '', type: undefined });
  const [start, setStart] = useState<any>(null);
  const [end, setEnd] = useState<any>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentViewName, setCurrentViewName] = useState('Month');
  const [endDate, setEndDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [note, setNote] = useState('');

  const [loadPendingApprovals, { data, loading, refetch }]: any = useLazyQuery(
    getPendingApprovals,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const [doApprove] = useMutation(approveDocument);
  const [doReject] = useMutation(rejectDocument);

  useEffect(() => {
    loadPendingApprovals();
  }, []);

  const currentViewNameChange = (e: any) => setCurrentViewName(e.target.value);
  const currentDateChange = (curDate: any) => setCurrentDate(curDate);
  const endDateChange = (curDate: any) => setEndDate(curDate);

  const displayedRows = React.useMemo(() => {
    // Tasks only (no purchase orders)
    let filtered = rows.filter((r: any) => !r.opType);
    if (start && end) {
      const s = new Date(start).setHours(0, 0, 0, 0);
      const e = new Date(end).setHours(23, 59, 59, 999);
      filtered = filtered.filter((r: any) => {
        const t = new Date(r.createdAt).getTime();
        return t >= s && t <= e;
      });
    }
    return filtered;
  }, [rows, start, end]);

  useEffect(() => {
    if (data?.getPendingApprovals?.data) {
      setRows(data.getPendingApprovals.data);
    }
  }, [data]);

  const handleOpenDialog = (doc: any, type: 'approve' | 'reject') => {
    setSelectedDoc(doc);
    setActionType(type);
    setNote('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDoc(null);
    setNote('');
  };

  const handleAction = async () => {
    if (!selectedDoc) return;

    try {
      const documentType = selectedDoc.opType ? 'purchase' : 'task';

      if (actionType === 'approve') {
        const res = await doApprove({
          variables: {
            documentId: selectedDoc._id,
            documentType,
            action: 'approve',
            note,
          },
        });
        if (res?.data?.approveDocument?.ok) {
          successAlert(setAlrt, isRTL);
          refetch();
        } else {
          errorAlert(setAlrt, isRTL);
        }
      } else {
        const res = await doReject({
          variables: {
            documentId: selectedDoc._id,
            documentType,
            note,
          },
        });
        if (res?.data?.rejectDocument?.ok) {
          successAlert(setAlrt, isRTL);
          refetch();
        } else {
          errorAlert(setAlrt, isRTL);
        }
      }
    } catch (error) {
      console.log(error);
      errorAlert(setAlrt, isRTL);
    }

    handleCloseDialog();
  };

  const ActionsProvider = (props: any) => (
    <DataTypeProvider
      for={['actions']}
      formatterComponent={({ row }: any) =>
        isManager ? (
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              variant="contained"
              size="small"
              style={{
                backgroundColor: '#4caf50',
                color: 'white',
                minWidth: 80,
              }}
              startIcon={<CheckIcon />}
              onClick={() => handleOpenDialog(row, 'approve')}
            >
              {isRTL ? 'اعتماد' : 'Approve'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              style={{
                borderColor: '#f44336',
                color: '#f44336',
                minWidth: 70,
              }}
              startIcon={<CloseIcon />}
              onClick={() => handleOpenDialog(row, 'reject')}
            >
              {isRTL ? 'رفض' : 'Reject'}
            </Button>
          </Box>
        ) : null
      }
      {...props}
    />
  );

  const StatusProvider = (props: any) => (
    <DataTypeProvider
      for={['approvalStatus']}
      formatterComponent={({ value }: any) => (
        <Chip
          label={getStatusLabel(value || 0, isRTL)}
          style={{
            backgroundColor: getStatusColor(value || 0),
            color: 'white',
            fontWeight: 'bold',
          }}
          size="small"
        />
      )}
      {...props}
    />
  );

  const CustomerProvider = (props: any) => (
    <DataTypeProvider
      for={['customerName']}
      formatterComponent={({ row }: any) => (
        <span>{isRTL ? row.customerNameAr : row.customerName}</span>
      )}
      {...props}
    />
  );

  const SupplierProvider = (props: any) => (
    <DataTypeProvider
      for={['supplierName']}
      formatterComponent={({ row }: any) => (
        <span>{isRTL ? row.supplierNameAr : row.supplierName}</span>
      )}
      {...props}
    />
  );

  const ContractProvider = (props: any) => (
    <DataTypeProvider
      for={['contractName']}
      formatterComponent={({ row }: any) => (
        <span>{isRTL ? row.contractNameAr : row.contractName}</span>
      )}
      {...props}
    />
  );

  const EmployeeProvider = (props: any) => (
    <DataTypeProvider
      for={['employeeName']}
      formatterComponent={({ row }: any) => (
        <span>{isRTL ? row.employeeNameAr : row.employeeName}</span>
      )}
      {...props}
    />
  );

  return (
    <Box style={{ padding: 20, height: '100%' }}>
      <Paper
        elevation={3}
        style={{
          padding: 20,
          borderRadius: 15,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography
            variant="h5"
            style={{
              color: theme?.palette?.primary?.main,
              fontWeight: 'bold',
            }}
          >
            {isRTL ? 'إدارة الاعتمادات' : 'Approval Management'}
          </Typography>

          <DateNavigatorReports
            setStart={setStart}
            setEnd={setEnd}
            currentDate={currentDate}
            currentDateChange={currentDateChange}
            currentViewName={currentViewName}
            currentViewNameChange={currentViewNameChange}
            endDate={endDate}
            endDateChange={endDateChange}
            views={[1, 7, 30, 365, 1000]}
            isRTL={isRTL}
            words={words}
            theme={theme}
          />
        </Box>

        {/* Grid */}
        <Box style={{ flex: 1, overflow: 'auto' }}>
          <Grid rows={displayedRows} columns={columns} getRowId={getRowId}>
            <SortingState />
            <SearchState />
            <PagingState defaultCurrentPage={0} defaultPageSize={10} />
            <IntegratedSorting />
            <IntegratedFiltering />
            <IntegratedPaging />
            <ActionsProvider />
            <StatusProvider />
            <CustomerProvider />
            <SupplierProvider />
            <ContractProvider />
            <EmployeeProvider />
            <DataTypeProvider
              for={['amount']}
              formatterComponent={currencyFormatter}
            />
            <DataTypeProvider
              for={['createdAt']}
              formatterComponent={timeFormatter}
            />
            <Table
              messages={{
                noData: loading
                  ? isRTL
                    ? 'جاري التحميل...'
                    : 'Loading...'
                  : isRTL
                    ? 'لا توجد مستندات تحتاج اعتماد'
                    : 'No documents pending approval',
              }}
              columnExtensions={tableColumnExtensions}
            />
            <TableColumnReordering
              defaultOrder={columns.map((c: any) => c.name)}
            />
            <TableColumnResizing defaultColumnWidths={tableColumnExtensions} />
            <TableHeaderRow
              showSortingControls
              titleComponent={({ children }: any) => (
                <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>
                  {children}
                </Typography>
              )}
            />
            <Toolbar />
            <SearchPanel
              inputComponent={(props: any) => (
                <SearchTable isRTL={isRTL} {...props} />
              )}
            />
            <PagingPanel pageSizes={[5, 10, 20, 50]} />
          </Grid>
        </Box>

        {alrt.show && (
          <AlertLocal
            isRTL={isRTL}
            type={alrt?.type}
            msg={alrt?.msg}
            top={-100}
          />
        )}
      </Paper>

      {/* Approval/Rejection Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === 'approve'
            ? isRTL
              ? 'اعتماد المستند'
              : 'Approve Document'
            : isRTL
              ? 'رفض المستند'
              : 'Reject Document'}
        </DialogTitle>
        <DialogContent>
          {selectedDoc && (
            <Box mb={2}>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                {isRTL ? 'رقم المستند: ' : 'Document No: '}
                {selectedDoc.docNo}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedDoc.title}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label={isRTL ? 'ملاحظات' : 'Notes'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              actionType === 'reject'
                ? isRTL
                  ? 'يرجى ذكر سبب الرفض...'
                  : 'Please provide reason for rejection...'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleAction}
            variant="contained"
            style={{
              backgroundColor: actionType === 'approve' ? '#4caf50' : '#f44336',
              color: 'white',
            }}
          >
            {actionType === 'approve'
              ? isRTL
                ? 'تأكيد الاعتماد'
                : 'Confirm Approval'
              : isRTL
                ? 'تأكيد الرفض'
                : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalManagement;
